/**
 * Add a provider (slice 1c spec §6.1): a dialog on a desktop, a sheet from
 * the bottom on a phone, in two steps. First the company (OpenAI, Anthropic,
 * xAI, OpenRouter, Cloudflare), then how to pay for it: a plan the owner
 * already has, or a key billed per use. A company with one way goes straight
 * to its form. The connect panel is the page's own (`ConnectSlot`), so a
 * sign-in's code shows here and a connect started here is followed when the
 * window closes. What each step says comes from `ai-view.ts`.
 */
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from "@crew/ui/components/drawer";
import { RadioGroup, RadioGroupCard } from "@crew/ui/components/radio-group";
import { StatusBadge } from "@crew/ui/components/status";
import { useIsMobile } from "@crew/ui/hooks/use-mobile";
import { cn } from "@crew/ui/lib/utils";
import { Check, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, type ReactNode } from "react";
import type { AiProvider, Me } from "../../app/me";
import { ProviderLogo } from "../setup/provider-tiles";
import { Show } from "./ai-parts";
import {
  makerRows,
  makerWays,
  MAKERS,
  onlyWay,
  stepLine,
  type Maker,
  type MakerRow,
  type Way,
} from "./ai-view";

type Ai = NonNullable<Me["computer"]>["ai"];

/** A maker's mark: its API's (OpenAI, Anthropic) or its only way's. */
const markOf = (maker: Maker): AiProvider => maker.ways[maker.ways.length - 1];

function Tag({ children }: { children: ReactNode }) {
  return (
    <span className="rounded-sm bg-muted px-1.5 py-0.5 text-2xs leading-3.5 font-medium text-muted-foreground">
      {children}
    </span>
  );
}

/** Step 1's row: the maker, its ways as tags, and what's connected; a maker with no way left is dim. */
function MakerButton({
  row,
  disabled,
  onPick,
}: {
  row: MakerRow;
  disabled: boolean;
  onPick: () => void;
}) {
  const { maker } = row;
  return (
    <li>
      <button
        type="button"
        data-maker={maker.id}
        disabled={disabled || !row.open}
        onClick={onPick}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-3 text-left transition-colors hover:bg-foreground/3 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:hover:bg-transparent"
      >
        <ProviderLogo
          provider={markOf(maker)}
          className={cn("size-8", !row.open && "opacity-60")}
        />
        <span className="flex min-w-0 flex-1 flex-col gap-1 sm:gap-0">
          <span
            className={cn(
              "text-[15px] leading-5 font-medium",
              row.open ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {maker.name}
          </span>
          <span className="flex flex-wrap items-center gap-1.5 sm:hidden">
            {row.tags.map((t) => (
              <Tag key={t}>{t}</Tag>
            ))}
          </span>
          <span className="text-meta text-muted-foreground">
            {[row.open ? maker.about : null, row.note].filter(Boolean).join(" · ")}
          </span>
        </span>
        {row.open ? (
          <>
            <span className="hidden shrink-0 gap-1.5 sm:flex">
              {row.tags.map((t) => (
                <Tag key={t}>{t}</Tag>
              ))}
            </span>
            <ChevronRight className="size-4 shrink-0 text-muted-foreground" />
          </>
        ) : (
          <StatusBadge tone="done" className="h-6 shrink-0 rounded-full px-2.5">
            Connected
          </StatusBadge>
        )}
      </button>
    </li>
  );
}

/** A way's card in step 2: how it's paid, what it is, and what's connected (or why it can't be). */
function WayCard({ way, checked }: { way: Way; checked: boolean }) {
  const connected = way.connected > 0 && way.blocked === null;
  const accounts = `${way.connected} account${way.connected === 1 ? "" : "s"} connected`;
  const line = way.blocked ?? (connected ? accounts : way.line);
  return (
    <RadioGroupCard
      value={way.provider}
      disabled={way.blocked !== null}
      data-way={way.provider}
      className={cn(
        "flex flex-1 flex-col items-start gap-2 rounded-lg px-3.5 py-3 text-left",
        checked
          ? "bg-foreground/[0.024] shadow-[0_0_0_1.5px_var(--color-foreground)]"
          : "bg-card shadow-[0_0_0_1px_rgb(31_42_68/0.14)]",
      )}
    >
      <Tag>{way.tag}</Tag>
      <span className="text-body font-medium text-foreground">{way.title}</span>
      <span
        className={cn(
          "flex items-start gap-1.5 text-meta",
          connected ? "text-moss" : "text-muted-foreground",
        )}
        data-route-note={way.held ? "" : undefined}
      >
        <Show when={connected}>
          <Check className="mt-0.5 size-3.5 shrink-0" />
        </Show>
        {line}
      </span>
    </RadioGroupCard>
  );
}

/** Step 2: how to pay, when there's a choice, then the form for the way picked. */
function WayStep({
  ways,
  picked,
  busy,
  slot,
  onWay,
}: {
  ways: Way[];
  picked: AiProvider | null;
  busy: boolean;
  slot: ReactNode;
  onWay: (provider: AiProvider) => void;
}) {
  return (
    <div className="flex flex-col gap-4">
      {/* One way has no choice, even once it's connected (a connect started here). */}
      <Show when={ways.length > 1}>
        <RadioGroup
          aria-label="How to connect"
          value={picked}
          disabled={busy}
          onValueChange={(v) => onWay(v as AiProvider)}
          className="flex flex-col gap-2.5 sm:flex-row"
        >
          {ways.map((way) => (
            <WayCard key={way.provider} way={way} checked={picked === way.provider} />
          ))}
        </RadioGroup>
      </Show>
      {picked ? slot : null}
    </div>
  );
}

/** The window's title and line, and the back arrow on step 2. */
function Head({
  mobile,
  maker,
  ways,
  busy,
  onBack,
}: {
  mobile: boolean;
  maker: Maker | null;
  ways: Way[];
  busy: boolean;
  onBack: () => void;
}) {
  const TitleEl = mobile ? DrawerTitle : DialogTitle;
  const LineEl = mobile ? DrawerDescription : DialogDescription;
  return (
    // Room for the dialog's ×; the sheet has none.
    <div className={cn("flex items-center gap-3", !mobile && "pr-8")}>
      <Show when={maker}>
        <button
          type="button"
          aria-label="Back to providers"
          disabled={busy}
          onClick={onBack}
          className="-ml-1.5 flex size-8 shrink-0 items-center justify-center rounded-full text-foreground hover:bg-muted focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:opacity-50"
        >
          <ChevronLeft className="size-4" />
        </button>
        {maker ? <ProviderLogo provider={markOf(maker)} className="hidden size-8 sm:flex" /> : null}
      </Show>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <TitleEl className="text-[17px] leading-6 font-medium text-foreground">
          {maker ? `Connect ${maker.name}` : "Add a provider"}
        </TitleEl>
        <LineEl className="text-meta text-pretty text-muted-foreground">
          {stepLine(maker, ways)}
        </LineEl>
      </div>
    </div>
  );
}

/**
 * The window's two steps. `picked` is the way the connect panel is open
 * for; `onWay` opens it, `onBack` closes it.
 */
function Steps({
  ai,
  mobile,
  picked,
  busy,
  slot,
  onWay,
  onBack,
}: {
  ai: Ai;
  mobile: boolean;
  picked: AiProvider | null;
  busy: boolean;
  slot: ReactNode;
  onWay: (provider: AiProvider) => void;
  onBack: () => void;
}) {
  const [chosen, setChosen] = useState<Maker | null>(null);
  // A way already open (a connect started here) keeps its maker.
  const maker = chosen ?? (picked ? (MAKERS.find((m) => m.ways.includes(picked)) ?? null) : null);
  const ways = maker ? makerWays(maker, ai) : [];
  const pick = (next: Maker) => {
    setChosen(next);
    const only = onlyWay(makerWays(next, ai));
    if (only) onWay(only);
  };
  return (
    <div className="flex flex-col gap-4" data-add-provider={maker?.id ?? "pick"}>
      <Head
        mobile={mobile}
        maker={maker}
        ways={ways}
        busy={busy}
        onBack={() => {
          setChosen(null);
          onBack();
        }}
      />
      {maker ? (
        <div className="border-t border-border pt-4">
          <WayStep ways={ways} picked={picked} busy={busy} slot={slot} onWay={onWay} />
        </div>
      ) : (
        <ul className="-mx-3 flex flex-col gap-0.5 border-t border-border pt-2">
          {makerRows(ai).map((row) => (
            <MakerButton
              key={row.maker.id}
              row={row}
              disabled={busy}
              onPick={() => pick(row.maker)}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

/** The window: shown while `show`; closing it leaves a running connect to its row. */
export function AddProviderWindow({
  ai,
  show,
  picked,
  busy,
  slot,
  onWay,
  onBack,
  onClose,
}: {
  ai: Ai;
  show: boolean;
  picked: AiProvider | null;
  busy: boolean;
  slot: ReactNode;
  onWay: (provider: AiProvider) => void;
  onBack: () => void;
  onClose: () => void;
}) {
  const mobile = useIsMobile();
  // Each opening starts at step 1; closing keeps the step on screen while the window animates out.
  const [opens, setOpens] = useState(0);
  const [shown, setShown] = useState(show);
  if (show !== shown) {
    setShown(show);
    if (show) setOpens((n) => n + 1);
  }
  const steps = (
    <Steps
      key={opens}
      ai={ai}
      mobile={mobile}
      picked={picked}
      busy={busy}
      slot={slot}
      onWay={onWay}
      onBack={onBack}
    />
  );
  if (mobile) {
    return (
      <Drawer open={show} onOpenChange={(o) => !o && onClose()} showSwipeHandle>
        <DrawerContent className="data-[swipe-direction=down]:rounded-t-2xl">
          <div className="max-h-[85dvh] overflow-y-auto px-4 pt-2 pb-6">{steps}</div>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={show} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90dvh] gap-0 overflow-y-auto p-6 sm:max-w-[560px]">
        {steps}
      </DialogContent>
    </Dialog>
  );
}
